import React, { useState, useMemo } from 'react';

import { notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getAge, getNow, showShortDate } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import ContactsExporter from '@components/ContactsExporter/ContactsExporter';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import { NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import StyleVariables from '@components/Gui/StyleVariables.js';
import MassiveExpenseFormContact from '@components/Transactions/TransactionsCreateForms/MassiveExpenseFormContact';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FixedFooter = styled.div`
    position: fixed;
    bottom: 0;
    left: ${(props) =>
        props.$isDrawer ? '' : props.$leftMargin ? props.$leftMargin : props.$size === 'small' ? '200px' : '300px'};
    background-color: #fff;
    padding: 16px 24px;
    box-sizing: border-box;
    z-index: 4;
    width: ${(props) =>
        props.$isDrawer
            ? ''
            : props.$leftMargin
              ? `calc(100vw - ${props.$leftMargin}px)`
              : props.size === 'small'
                ? 'calc(100vw - 200px)'
                : 'calc(100vw - 300px)'};
    border: ${StyleVariables.DefaultBorder};
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    flex-wrap: wrap;
    text-transform: uppercase;
    align-items: center;
`;

const FooterItems = styled.div`
    display: flex;
    align-items: center;
`;

const SelectContainer = styled.div`
    margin-left: 16px;
`;

// sezione del drawer di export: titolo + pulsante, separata da bordo inferiore
const ExportSection = styled.div`
    padding-bottom: 1.6rem;
    margin-bottom: 1.6rem;
    border-bottom: 1px solid #f0f0f0;

    p {
        margin: 0 0 1rem 0;
    }
`;

export const ModelsWallAccountingFooter = ({ models }) => {
    const { breakpoints } = useLayoutContext();

    const { actions, layout } = useContactsSelector();
    const { selectedItemsIDs, multipleSelectionActive } = actions;

    const disabledItems = useMemo(() => {
        return models
            .filter((model) => !model.Approved || !model.BillingInfos || model.BillingInfos.length === 0)
            .map((model) => model.ID);
    }, [models]);

    const cs_dispatch = useContactsSelectorDispatch();

    const [showMassiveExpenseDrawer, setShowMassiveExpenseDrawer] = useState(false);
    const [showDownloadDrawer, setShowDownloadDrawer] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [addressBookExportLoading, setAddressBookExportLoading] = useState(false);

    const ExportModelsList = useUserPermission('ExportModelsList');
    const CreateStatementRowOnModel = useUserPermission('CreateStatementRowOnModel');

    const handleSelectAll = () => {
        let newmodels = disabledItems
            ? models.filter((model) => !disabledItems.find((item) => item === model.ID))
            : models;
        const newSelectedList = newmodels.map((model) => model.ID);
        cs_dispatch({
            type: 'UpdateSelectedItems',
            newlist: [...newSelectedList],
        });
    };

    const handleOpenMassiveExpenseDrawer = () => {
        if (!CreateStatementRowOnModel) {
            return;
        }
        setShowMassiveExpenseDrawer(true);
    };

    const handleOpenDownloadDrawer = () => {
        setShowDownloadDrawer(true);
    };

    const handleCloseDownloadDrawer = () => {
        setShowDownloadDrawer(false);
    };

    const handleExportModels = () => {
        if (!ExportModelsList || exportLoading) {
            return;
        }
        setExportLoading(true);
        Axios.post('/excel/models/export', { ContactIds: selectedItemsIDs }, { responseType: 'blob', timeout: 30000 })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = `models-${getNow().format('DDMMMYYYY')}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                setExportLoading(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error exporting models',
                    description: error.message,
                });
                setExportLoading(false);
            });
    };

    const handleExportAddressBook = () => {
        if (!ExportModelsList || addressBookExportLoading) {
            return;
        }
        setAddressBookExportLoading(true);
        Axios.post(
            '/Excel/Models/AddressBook/Export',
            { ContactIds: selectedItemsIDs },
            { responseType: 'blob', timeout: 30000 }
        )
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = `addressbook-${getNow().format('DDMMMYYYY')}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                setAddressBookExportLoading(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error exporting addressbook',
                    description: error.message,
                });
                setAddressBookExportLoading(false);
            });
    };

    const onClearSelection = () => {
        cs_dispatch({
            type: 'ClearSelectedItems',
        });
    };

    // la ricerca accounting non espone Email/Mobile/Country a livello di record: vanno presi dal billing info di default
    const getDefaultBillingInfo = (model) => {
        const billingInfos = model.BillingInfos || [];
        return billingInfos.find((info) => info.ID === model.DefaultBillingInfoID) || billingInfos[0] || {};
    };

    const selectedModelsData = useMemo(() => {
        const selectedModels = selectedItemsIDs.map((modelId) => {
            const model = models.find((model) => model.ID === modelId);
            if (model) {
                const billingInfo = getDefaultBillingInfo(model);
                return {
                    ...model,
                    id: model.ID,
                    name: model.Name,
                    surname: model.Surname,
                    age: getAge(model.BirthDate),
                    email: billingInfo.ContactEmail,
                    mobile: billingInfo.ContactPhone,
                    city: billingInfo.City,
                    country: billingInfo.Country,
                    vatNumber: billingInfo.VatNumber,
                    invoiceType: billingInfo.InvoiceType,
                    motherAgency: model.MotherAgencyName,
                    birthdate: model.BirthDate ? showShortDate(model.BirthDate) : null,
                    nationality: model.NationalityCode,
                };
            }
            return null;
        });
        return selectedModels;
    }, [models, selectedItemsIDs]);

    return (
        <FixedFooter
            $size={breakpoints < 4 ? 'small' : 'medium'}
            $leftMargin={layout.leftMargin}
            $isDrawer={layout.drawer}
        >
            <FooterItems>
                <div>
                    {selectedItemsIDs.length} Model{selectedItemsIDs.length > 1 && 's'} selected
                </div>
                &nbsp;
                {multipleSelectionActive && (
                    <SelectContainer>
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['check-double']}
                            label="select all"
                            onClick={handleSelectAll}
                        />
                        &nbsp;
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['times']}
                            label="clear selection"
                            onClick={onClearSelection}
                        />
                    </SelectContainer>
                )}
            </FooterItems>
            <FooterItems>
                <NwButton
                    disabled={selectedItemsIDs.length === 0}
                    ghost
                    label="export"
                    icon={byPrefixAndName.fasl['file-spreadsheet']}
                    onClick={handleOpenDownloadDrawer}
                />
                &nbsp;
                <NwButton
                    disabled={selectedItemsIDs.length === 0 || !CreateStatementRowOnModel}
                    ghost
                    label="add expense"
                    icon={byPrefixAndName.fasl['file-spreadsheet']}
                    onClick={handleOpenMassiveExpenseDrawer}
                />
                {showMassiveExpenseDrawer && (
                    <NwDrawer
                        title={'Add Massive Expense'}
                        width="40vw"
                        minWidth="600px"
                        onClose={() => setShowMassiveExpenseDrawer(false)}
                    >
                        <MassiveExpenseFormContact
                            contactIDs={selectedItemsIDs}
                            onCloseDrawer={() => setShowMassiveExpenseDrawer(false)}
                            width="40vw"
                            minWidth="600px"
                        />
                    </NwDrawer>
                )}
                {showDownloadDrawer && (
                    <NwDrawer title={'Export Models'} onClose={handleCloseDownloadDrawer}>
                        <ExportSection>
                            <p>
                                <strong>Export full dataset</strong>
                            </p>
                            <NwButton
                                disabled={!ExportModelsList}
                                loading={exportLoading}
                                primary
                                icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                label="export full dataset"
                                onClick={handleExportModels}
                            />
                        </ExportSection>
                        <ExportSection>
                            <p>
                                <strong>Export addressbook</strong>
                            </p>
                            <NwButton
                                disabled={!ExportModelsList}
                                loading={addressBookExportLoading}
                                primary
                                icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                label="export addressbook"
                                onClick={handleExportAddressBook}
                            />
                        </ExportSection>
                        <ContactsExporter
                            type="model"
                            headers={[
                                { label: 'Id', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Surname', key: 'surname' },
                                { label: 'E-mail address', key: 'email' },
                                { label: 'Mobile', key: 'mobile' },
                                { label: 'Mother Agency', key: 'motherAgency' },
                                { label: 'Age', key: 'age' },
                                { label: 'Birthdate', key: 'birthdate' },
                                { label: 'Nationality', key: 'nationality' },
                                { label: 'Billing Country', key: 'country' },
                                { label: 'Billing City', key: 'city' },
                                { label: 'VAT Number', key: 'vatNumber' },
                                { label: 'Invoice Type', key: 'invoiceType' },
                            ]}
                            data={selectedModelsData}
                        />
                    </NwDrawer>
                )}
            </FooterItems>
        </FixedFooter>
    );
};
